import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  Stack,
  Alert,
  CircularProgress,
  Container,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  OutlinedInput,
  Tooltip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Grid,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import PrintIcon from "@mui/icons-material/Print";
import ShareIcon from "@mui/icons-material/Share";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SmsIcon from "@mui/icons-material/Sms";
import { authenticatedFetch } from "../../utils/api";
import { usePermissions } from "../../utils/permissions";
import DiaCircle from "../DiaCircle";
import GaugeChip from "../GaugeChip";
import ProgressBar from "../ProgressBar";
import { formatInteger } from "../../utils/formatters";
import { useTranslation } from "../../i18n";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

interface Company {
  id: number;
  name: string;
}

interface ReceivedNote {
  id: number;
  total_bags: number;
  total_weight: number;
  counts_display: string;
}

interface Machine {
  id: number;
  dia: number;
  gauge: string;
  machine_type: string;
}

interface ProgramDetail {
  id?: number;
  dia: number;
  gauge: string;
  program_type: string;
  weight_in_kilo: number;
  number_of_rolls?: number;
  texture?: number;
  design?: string;
  status?: string;
  completed_weight?: number;
  progress_percentage?: number;
  remaining_weight?: number;
  _destroy?: boolean;
}

interface EmployeeShiftDetail {
  id: number;
  program_id: number;
  dia: string;
  gauge: string;
  machine_type: string;
  rolls: number;
  weight: number;
  employee_shift: {
    id: number;
    shift_date: string;
    shift_time: string;
    employee: {
      id: number;
      name: string;
    };
  };
}

interface DeliveryDetail {
  id: number;
  dia: string;
  gauge: string;
  rolls: number;
  weight: number;
}

interface Delivery {
  id: number;
  delivery_person_name: string;
  delivery_person_phone: string;
  delivery_vehicle_number: string;
  created_at: string;
  delivery_details: DeliveryDetail[];
  calculated_total_weight: number;
  calculated_total_rolls: number;
  specs_display: string;
}

interface Program {
  id: number;
  company_id: number;
  company: {
    id: number;
    name: string;
  };
  program_details: ProgramDetail[];
  lot?: string;
  status: "not_started" | "in_progress" | "completed" | "delivered";
  received_notes?: ReceivedNote[];
  deliveries?: Delivery[];
  created_at: string;
  total_weight: number;
  specs_display: string;
  types_display: string;
  designs_display: string;
  employee_shift_details?: EmployeeShiftDetail[];
}

const createProgramDetailSchema = (machines: Machine[]) =>
  z
    .object({
      id: z.number().optional(),
      dia: z
        .number({ required_error: "Dia is required" })
        .int("Dia must be an integer")
        .positive("Dia must be positive"),
      gauge: z.enum(["14", "16"], {
        required_error: "Gauge is required",
      }),
      program_type: z.enum(["rib", "fine"], {
        required_error: "Type is required",
      }),
      weight_in_kilo: z
        .number({ required_error: "Weight is required" })
        .positive("Weight must be positive"),
      number_of_rolls: z
        .union([z.number(), z.string(), z.undefined(), z.null()])
        .transform((val) => val === "" || val === null || val === undefined ? undefined : Number(val))
        .optional()
        .refine((val) => val === undefined || val > 0, "Number of rolls must be positive"),
      design: z.string().optional(),
      texture: z
        .union([z.number(), z.string(), z.undefined(), z.null()])
        .transform((val) => val === "" || val === null || val === undefined ? undefined : Number(val))
        .optional()
        .refine((val) => val === undefined || (Number.isInteger(val) && val > 0), "Texture must be a positive integer"),
      _destroy: z.boolean().optional(),
    })
    .refine(
      (data) => {
        // Skip validation if marked for destruction
        if (data._destroy) return true;
        // Check if the combination of dia, gauge, and program_type exists in machines
        return machines.some(
          (machine) =>
            machine.dia === data.dia &&
            machine.gauge === data.gauge &&
            machine.machine_type === data.program_type
        );
      },
      {
        message:
          "This combination of Dia, Gauge, and Type does not exist in available machines",
        path: ["dia"], // Show error on dia field
      }
    );

const createProgramSchema = (machines: Machine[]) =>
  z.object({
    company_id: z.number().int(),
    lot: z.string().optional(),
    status: z.enum(["not_started", "in_progress", "completed", "delivered"], {
      required_error: "Status is required",
    }),
    received_note_ids: z.array(z.number()).optional(),
    program_details_attributes: z
      .array(createProgramDetailSchema(machines))
      .min(1, "At least one program detail is required"),
  });

type ProgramFormData = z.infer<ReturnType<typeof createProgramSchema>>;

export default function ProgramDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { canUpdate, canDelete, isCompanyRep, canAccessResource } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [isEditing, setIsEditing] = useState(false);
  const [program, setProgram] = useState<Program | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [receivedNotes, setReceivedNotes] = useState<ReceivedNote[]>([]);
  const [filteredReceivedNotes, setFilteredReceivedNotes] = useState<ReceivedNote[]>([]);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("Link copied to clipboard!");
  const [shareUrl, setShareUrl] = useState("");
  const [smsMessage, setSmsMessage] = useState("");

  const form = useForm<ProgramFormData>();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setError: setFieldError,
    clearErrors,
    setValue,
  } = form;

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "program_details_attributes",
  });

  const programDetails = watch("program_details_attributes");
  const selectedCompanyId = watch("company_id");

  useEffect(() => {
    fetchProgram();
    fetchCompanies();
    fetchReceivedNotes();
    fetchMachines();
  }, [id]);

  // Validate machine combinations when machines or program details change
  useEffect(() => {
    if (machines.length > 0 && isEditing) {
      const details = watch("program_details_attributes");
      details?.forEach((detail, index) => {
        if (
          detail &&
          detail.dia &&
          detail.gauge &&
          detail.program_type &&
          !detail._destroy
        ) {
          const machineExists = machines.some(
            (machine) =>
              machine.dia === detail.dia &&
              machine.gauge === detail.gauge &&
              machine.machine_type === detail.program_type
          );
          if (!machineExists) {
            setFieldError(`program_details_attributes.${index}.dia`, {
              type: "manual",
              message: `Dia ${detail.dia} with ${detail.gauge} GG and ${detail.program_type} type is not available in machines`,
            });
          } else {
            clearErrors(`program_details_attributes.${index}.dia`);
          }
        }
      });
    }
  }, [machines, programDetails, isEditing, setFieldError, clearErrors, watch]);

  useEffect(() => {
    if (selectedCompanyId) {
      const filtered = receivedNotes.filter(
        (receivedNote: any) => receivedNote.company?.id === selectedCompanyId
      );
      setFilteredReceivedNotes(filtered);
    } else {
      setFilteredReceivedNotes([]);
    }
    // Clear selected receipts when company changes (only in edit mode)
    if (isEditing) {
      setValue("received_note_ids", []);
    }
  }, [selectedCompanyId, receivedNotes, isEditing, setValue]);

  const fetchProgram = async () => {
    try {
      const response = await authenticatedFetch(`${API_URL}/programs/${id}`);
      if (!response.ok) {
        throw new Error("Failed to fetch program");
      }

      const data = await response.json();
      setProgram(data);
      reset({
        company_id: data.company_id,
        lot: data.lot || "",
        status: data.status || "not_started",
        received_note_ids: data.received_notes?.map((r: any) => r.id) || [],
        program_details_attributes: data.program_details.map(
          (d: ProgramDetail) => ({
            id: d.id,
            dia: Number(d.dia),
            gauge: String(d.gauge),
            program_type: d.program_type,
            weight_in_kilo: Number(d.weight_in_kilo),
            number_of_rolls: d.number_of_rolls ? Number(d.number_of_rolls) : undefined,
            design: d.design || "",
          })
        ),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await authenticatedFetch(`${API_URL}/companies?per_page=1000&minimal=true`);
      if (response.ok) {
        const data = await response.json();
        setCompanies(data);
      }
      // Silently handle failure - user may not have access to companies list
    } catch (err) {
      console.error("Error fetching companies:", err);
    }
  };

  const fetchReceivedNotes = async () => {
    try {
      const response = await authenticatedFetch(`${API_URL}/received_notes?per_page=1000`);
      if (response.ok) {
        const data = await response.json();
        setReceivedNotes(data);
      }
      // Silently handle failure - user may not have access to received notes list
    } catch (err) {
      console.error("Error fetching received notes:", err);
    }
  };

  const fetchMachines = async () => {
    try {
      const response = await authenticatedFetch(`${API_URL}/machines`);
      if (response.ok) {
        const data = await response.json();
        setMachines(data);
      }
      // Silently handle failure - user may not have access to machines list
    } catch (err) {
      console.error("Error fetching machines:", err);
    }
  };

  const onSubmit = async (data: ProgramFormData) => {
    try {
      // Validate that all program detail combinations exist in machines
      let hasValidationError = false;
      data.program_details_attributes?.forEach((detail, index) => {
        // Skip validation for details marked for destruction
        if (detail._destroy) return;

        const machineExists = machines.some(
          (machine) =>
            machine.dia === detail.dia &&
            machine.gauge === detail.gauge &&
            machine.machine_type === detail.program_type
        );
        if (!machineExists) {
          setFieldError(`program_details_attributes.${index}.dia`, {
            type: "manual",
            message: `Dia ${detail.dia} with ${detail.gauge} GG and ${detail.program_type} type is not available in machines`,
          });
          hasValidationError = true;
        }
      });

      if (hasValidationError) {
        setError("Please fix the validation errors before submitting");
        return;
      }

      const response = await authenticatedFetch(`${API_URL}/programs/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ program: data }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(JSON.stringify(errorData));
      }

      const updatedProgram = await response.json();
      setProgram(updatedProgram);
      setIsEditing(false);
      setError("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this program?")) {
      try {
        const response = await authenticatedFetch(`${API_URL}/programs/${id}`, {
          method: "DELETE",
        });

        if (!response.ok) {
          throw new Error("Failed to delete program");
        }

        navigate("/programs");
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      }
    }
  };

  const handleRemoveDetail = (index: number) => {
    const detail = programDetails?.[index];
    if (detail?.id) {
      // For existing details (with id), mark as _destroy instead of removing
      // Use update() from useFieldArray to ensure _destroy is included in form submission
      update(index, { ...detail, _destroy: true });
    } else {
      // For new details (without id), just remove from the array
      remove(index);
    }
  };

  const calculateTotalWeight = () => {
    return programDetails
      ?.filter((detail) => !detail?._destroy)
      ?.reduce((sum, detail) => sum + (detail?.weight_in_kilo || 0), 0)
      .toFixed(2);
  };

  const calculateProgress = (detail: ProgramDetail) => {
    if (!program?.employee_shift_details) {
      return { 
        completedWeight: 0, 
        weightPercentage: 0,
        completedRolls: 0,
        rollsPercentage: 0,
        hasRollsConstraint: !!detail.number_of_rolls
      };
    }

    // Find all employee shift details matching this program detail
    const matchingShiftDetails = program.employee_shift_details.filter(
      (shiftDetail) =>
        shiftDetail.dia === String(detail.dia) &&
        shiftDetail.gauge === String(detail.gauge) &&
        shiftDetail.machine_type === detail.program_type
    );

    // Sum up the completed weight and rolls
    const completedWeight = matchingShiftDetails.reduce(
      (sum, shiftDetail) => sum + (Number(shiftDetail.weight) || 0),
      0
    );
    const completedRolls = matchingShiftDetails.reduce(
      (sum, shiftDetail) => sum + (Number(shiftDetail.rolls) || 0),
      0
    );

    const totalWeight = Number(detail.weight_in_kilo) || 0;
    const totalRolls = Number(detail.number_of_rolls) || 0;
    const weightPercentage = totalWeight > 0 ? (completedWeight / totalWeight) * 100 : 0;
    const rollsPercentage = totalRolls > 0 ? (completedRolls / totalRolls) * 100 : 0;

    return {
      completedWeight,
      weightPercentage: Math.min(weightPercentage, 100), // Cap at 100%
      completedRolls,
      rollsPercentage: Math.min(rollsPercentage, 100),
      hasRollsConstraint: !!detail.number_of_rolls
    };
  };

  const handlePrint = () => {
    window.print();
  };

  const handleShare = () => {
    if (program?.id) {
      // Get the share token from the program (it will be in the API response)
      const frontendUrl = window.location.origin;
      // For now, we'll need to fetch the program data again to get the share_token
      // Or we can add it to the initial program fetch
      fetchProgramWithShareToken();
    }
  };

  const fetchProgramWithShareToken = async () => {
    try {
      const response = await authenticatedFetch(`${API_URL}/programs/${id}`);
      const data = await response.json();
      const frontendUrl = window.location.origin;
      // Use shorter URL format /p/{token} for SMS
      const url = `${frontendUrl}/p/${(data as any).share_token}`;
      setShareUrl(url);
      
      // Generate SMS message
      const companyName = data.company?.name || "Customer";
      const totalWeight = data.total_weight || 0;
      const message = `${companyName}'s program for Devi Impex weighing ${formatInteger(totalWeight)} kg can be tracked here: ${url}. Thank you.`;
      setSmsMessage(message);
      
      setShareDialogOpen(true);
    } catch (err) {
      console.error("Failed to get share URL:", err);
      setError("Failed to generate share link");
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setSnackbarMessage("Link copied to clipboard!");
    setSnackbarOpen(true);
  };

  const handleCopySms = () => {
    navigator.clipboard.writeText(smsMessage);
    setSnackbarMessage("SMS message copied to clipboard!");
    setSnackbarOpen(true);
  };

  const handleCloseShareDialog = () => {
    setShareDialogOpen(false);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      <Card sx={{ mt: { xs: 2, sm: 4 } }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          <Box
            sx={{
              mb: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: { xs: "wrap", sm: "nowrap" },
              gap: 1,
            }}
          >
            <Typography variant="h5" component="h2" sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
              {isEditing ? t('programs.editTitle') : t('programs.details')}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
              {!isEditing && (
                <>
                  <IconButton
                    onClick={handleShare}
                    color="primary"
                    title={t('programs.share')}
                    className="no-print"
                    size="small"
                  >
                    <ShareIcon />
                  </IconButton>
                  <IconButton
                    onClick={handlePrint}
                    color="primary"
                    title={t('print')}
                    className="no-print"
                    size="small"
                  >
                    <PrintIcon />
                  </IconButton>
                </>
              )}
              <Button
                startIcon={<ArrowBackIcon sx={{ display: { xs: "none", sm: "inline-block" } }} />}
                onClick={() => navigate("/programs")}
                variant="outlined"
                className="no-print"
                size="small"
                sx={{ minWidth: { xs: "auto", sm: "64px" } }}
              >
                <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>{t('back')}</Box>
                <ArrowBackIcon sx={{ display: { xs: "inline-block", sm: "none" } }} />
              </Button>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {isEditing ? (
            <Box component="form" onSubmit={handleSubmit(onSubmit)}>
              <Controller
                name="company_id"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl
                    fullWidth
                    margin="normal"
                    error={!!fieldState.error}
                  >
                    <InputLabel>{t('programs.company')}</InputLabel>
                    <Select
                      {...field}
                      label={t('programs.company')}
                      value={field.value || ""}
                    >
                      {companies.map((company) => (
                        <MenuItem key={company.id} value={company.id}>
                          {company.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {fieldState.error && (
                      <FormHelperText>
                        {fieldState.error.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />

              <Controller
                name="lot"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={t('programs.lotOptional')}
                    fullWidth
                    margin="normal"
                    multiline
                    rows={2}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />

              <Controller
                name="status"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl
                    fullWidth
                    margin="normal"
                    error={!!fieldState.error}
                  >
                    <InputLabel>{t('status')}</InputLabel>
                    <Select
                      {...field}
                      label={t('status')}
                      value={field.value || "not_started"}
                    >
                      <MenuItem value="not_started">
                        <Chip
                          label={t('programs.statusNotStarted')}
                          size="small"
                          sx={{ bgcolor: "#9e9e9e", color: "#fff" }}
                        />
                      </MenuItem>
                      <MenuItem value="in_progress">
                        <Chip
                          label={t('programs.statusInProgress')}
                          size="small"
                          sx={{ bgcolor: "#fdd835", color: "#000" }}
                        />
                      </MenuItem>
                      <MenuItem value="completed">
                        <Chip
                          label={t('programs.statusCompleted')}
                          size="small"
                          sx={{ bgcolor: "#66bb6a", color: "#fff" }}
                        />
                      </MenuItem>
                      <MenuItem value="delivered">
                        <Chip
                          label={t('programs.statusDelivered')}
                          size="small"
                          sx={{ bgcolor: "#2196f3", color: "#fff" }}
                        />
                      </MenuItem>
                    </Select>
                    {fieldState.error && (
                      <FormHelperText>
                        {fieldState.error.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />

              <Box sx={{ mt: 3, mb: 2 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Typography variant="h6" sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}>{t('programs.programDetails')}</Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                  onClick={() =>
                    append({
                      dia: "" as any,
                      gauge: "" as any,
                      program_type: "" as any,
                      weight_in_kilo: "" as any,
                      number_of_rolls: undefined as any,
                      design: "",
                      texture: undefined as any,
                    })
                  }
                  >
                    <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>{t('addDetail')}</Box>
                    <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>{t('add')}</Box>
                  </Button>
                </Stack>

                {/* Desktop Table View */}
                <Box sx={{ display: { xs: "none", md: "block" } }}>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Dia</TableCell>
                          <TableCell>Gauge (GG)</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Weight (kg)</TableCell>
                          <TableCell>Rolls (Optional)</TableCell>
                          <TableCell>Texture (Optional)</TableCell>
                          <TableCell>Design</TableCell>
                          <TableCell width={60}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                      {fields.map((field, index) => {
                        // Skip rendering items marked for destruction
                        if (programDetails?.[index]?._destroy) {
                          return null;
                        }
                        return (
                        <TableRow key={field.id}>
                          <TableCell>
                            <Controller
                              name={`program_details_attributes.${index}.dia`}
                              control={control}
                              render={({ field, fieldState }) => {
                                // Get current gauge and program_type for this row
                                const currentGauge =
                                  programDetails?.[index]?.gauge;
                                const currentType =
                                  programDetails?.[index]?.program_type;

                                // Filter machines based on selected gauge and type
                                let availableDias = machines.map((m) => m.dia);
                                if (currentGauge && currentType) {
                                  availableDias = machines
                                    .filter(
                                      (m) =>
                                        m.gauge === currentGauge &&
                                        m.machine_type === currentType
                                    )
                                    .map((m) => m.dia);
                                } else if (currentGauge) {
                                  availableDias = machines
                                    .filter((m) => m.gauge === currentGauge)
                                    .map((m) => m.dia);
                                } else if (currentType) {
                                  availableDias = machines
                                    .filter(
                                      (m) => m.machine_type === currentType
                                    )
                                    .map((m) => m.dia);
                                }

                                const uniqueDias = Array.from(
                                  new Set(availableDias)
                                ).sort((a, b) => a - b);

                                return (
                                  <FormControl
                                    fullWidth
                                    size="small"
                                    error={!!fieldState.error}
                                  >
                                    <Select
                                      {...field}
                                      value={field.value || ""}
                                      onChange={(e) =>
                                        field.onChange(Number(e.target.value))
                                      }
                                      disabled={
                                        machines.length === 0 ||
                                        uniqueDias.length === 0
                                      }
                                    >
                                      {machines.length === 0 ? (
                                        <MenuItem value="" disabled>
                                          No machines available
                                        </MenuItem>
                                      ) : uniqueDias.length === 0 ? (
                                        <MenuItem value="" disabled>
                                          No machines for selected Gauge/Type
                                        </MenuItem>
                                      ) : (
                                        uniqueDias.map((dia) => (
                                          <MenuItem key={dia} value={dia}>
                                            {dia}
                                          </MenuItem>
                                        ))
                                      )}
                                    </Select>
                                    {machines.length === 0 &&
                                      !fieldState.error && (
                                        <FormHelperText>
                                          Please add machines first
                                        </FormHelperText>
                                      )}
                                    {machines.length > 0 &&
                                      uniqueDias.length === 0 &&
                                      !fieldState.error && (
                                        <FormHelperText>
                                          No machines available for selected
                                          Gauge/Type
                                        </FormHelperText>
                                      )}
                                    {fieldState.error && (
                                      <FormHelperText>
                                        {fieldState.error.message}
                                      </FormHelperText>
                                    )}
                                  </FormControl>
                                );
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Controller
                              name={`program_details_attributes.${index}.gauge`}
                              control={control}
                              render={({ field, fieldState }) => (
                                <FormControl
                                  fullWidth
                                  size="small"
                                  error={!!fieldState.error}
                                >
                                  <Select {...field} value={field.value || ""}>
                                    <MenuItem value="14">14 GG</MenuItem>
                                    <MenuItem value="16">16 GG</MenuItem>
                                    <MenuItem value="24">24 GG</MenuItem>
                                  </Select>
                                  {fieldState.error && (
                                    <FormHelperText>
                                      {fieldState.error.message}
                                    </FormHelperText>
                                  )}
                                </FormControl>
                              )}
                            />
                          </TableCell>
                          <TableCell>
                            <Controller
                              name={`program_details_attributes.${index}.program_type`}
                              control={control}
                              render={({ field, fieldState }) => (
                                <FormControl
                                  fullWidth
                                  size="small"
                                  error={!!fieldState.error}
                                >
                                  <Select {...field} value={field.value || ""}>
                                    <MenuItem value="rib">Rib</MenuItem>
                                    <MenuItem value="fine">Fine</MenuItem>
                                  </Select>
                                  {fieldState.error && (
                                    <FormHelperText>
                                      {fieldState.error.message}
                                    </FormHelperText>
                                  )}
                                </FormControl>
                              )}
                            />
                          </TableCell>
                          <TableCell>
                            <Controller
                              name={`program_details_attributes.${index}.weight_in_kilo`}
                              control={control}
                              render={({
                                field: { onChange, value, ...field },
                                fieldState,
                              }) => (
                                <TextField
                                  {...field}
                                  value={value || ""}
                                  onChange={(e) =>
                                    onChange(
                                      e.target.value
                                        ? Number(e.target.value)
                                        : undefined
                                    )
                                  }
                                  type="number"
                                  size="small"
                                  inputProps={{ step: "0.01" }}
                                  error={!!fieldState.error}
                                  helperText={fieldState.error?.message}
                                  fullWidth
                                />
                              )}
                            />
                          </TableCell>
                          <TableCell>
                            <Controller
                              name={`program_details_attributes.${index}.number_of_rolls`}
                              control={control}
                              render={({
                                field: { onChange, value, ...field },
                                fieldState,
                              }) => (
                                <TextField
                                  {...field}
                                  value={value || ""}
                                  onChange={(e) =>
                                    onChange(
                                      e.target.value
                                        ? Number(e.target.value)
                                        : undefined
                                    )
                                  }
                                  type="number"
                                  size="small"
                                  inputProps={{ step: "0.01" }}
                                  error={!!fieldState.error}
                                  helperText={fieldState.error?.message}
                                  fullWidth
                                />
                              )}
                            />
                          </TableCell>
                          <TableCell>
                            <Controller
                              name={`program_details_attributes.${index}.texture`}
                              control={control}
                              render={({
                                field: { onChange, value, ...field },
                                fieldState,
                              }) => (
                                <TextField
                                  {...field}
                                  value={value || ""}
                                  onChange={(e) =>
                                    onChange(
                                      e.target.value
                                        ? Number(e.target.value)
                                        : undefined
                                    )
                                  }
                                  type="number"
                                  size="small"
                                  inputProps={{ step: "1" }}
                                  error={!!fieldState.error}
                                  helperText={fieldState.error?.message}
                                  fullWidth
                                />
                              )}
                            />
                          </TableCell>
                          <TableCell>
                            <Controller
                              name={`program_details_attributes.${index}.design`}
                              control={control}
                              render={({ field, fieldState }) => (
                                <TextField
                                  {...field}
                                  size="small"
                                  error={!!fieldState.error}
                                  helperText={fieldState.error?.message}
                                  fullWidth
                                />
                              )}
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveDetail(index)}
                              disabled={programDetails?.filter(d => !d?._destroy).length === 1}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                      })}
                        <TableRow sx={{ bgcolor: "action.hover" }}>
                          <TableCell colSpan={3}>
                            <strong>Total Weight</strong>
                          </TableCell>
                          <TableCell>
                            <strong>{calculateTotalWeight()} kg</strong>
                          </TableCell>
                          <TableCell colSpan={4}></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                {/* Mobile Card View */}
                <Box sx={{ display: { xs: "block", md: "none" } }}>
                  <Stack spacing={2}>
                    {fields.map((field, index) => {
                      // Skip rendering items marked for destruction
                      if (programDetails?.[index]?._destroy) {
                        return null;
                      }
                      return (
                      <Card key={field.id} variant="outlined">
                        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                            <Typography variant="subtitle2" fontWeight="bold">
                              Detail #{index + 1}
                            </Typography>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveDetail(index)}
                              disabled={programDetails?.filter(d => !d?._destroy).length === 1}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>

                          <Grid container spacing={2}>
                            {/* Row 1: Dia, Gauge, Type */}
                            <Grid item xs={12} sm={4}>
                              <Controller
                                name={`program_details_attributes.${index}.dia`}
                                control={control}
                                render={({ field, fieldState }) => {
                                  const currentGauge = programDetails?.[index]?.gauge;
                                  const currentType = programDetails?.[index]?.program_type;
                                  let availableDias = machines.map((m) => m.dia);
                                  if (currentGauge && currentType) {
                                    availableDias = machines
                                      .filter((m) => m.gauge === currentGauge && m.machine_type === currentType)
                                      .map((m) => m.dia);
                                  } else if (currentGauge) {
                                    availableDias = machines.filter((m) => m.gauge === currentGauge).map((m) => m.dia);
                                  } else if (currentType) {
                                    availableDias = machines.filter((m) => m.machine_type === currentType).map((m) => m.dia);
                                  }
                                  const uniqueDias = Array.from(new Set(availableDias)).sort((a, b) => a - b);

                                  return (
                                    <FormControl fullWidth size="small" error={!!fieldState.error}>
                                      <InputLabel>Dia</InputLabel>
                                      <Select
                                        {...field}
                                        label="Dia"
                                        value={field.value || ""}
                                        onChange={(e) => field.onChange(Number(e.target.value))}
                                        disabled={machines.length === 0 || uniqueDias.length === 0}
                                      >
                                        {machines.length === 0 ? (
                                          <MenuItem value="" disabled>No machines available</MenuItem>
                                        ) : uniqueDias.length === 0 ? (
                                          <MenuItem value="" disabled>No machines for selected Gauge/Type</MenuItem>
                                        ) : (
                                          uniqueDias.map((dia) => (
                                            <MenuItem key={dia} value={dia}>{dia}</MenuItem>
                                          ))
                                        )}
                                      </Select>
                                      {fieldState.error && (
                                        <FormHelperText>{fieldState.error.message}</FormHelperText>
                                      )}
                                    </FormControl>
                                  );
                                }}
                              />
                            </Grid>

                            <Grid item xs={12} sm={4}>
                              <Controller
                                name={`program_details_attributes.${index}.gauge`}
                                control={control}
                                render={({ field, fieldState }) => (
                                  <FormControl fullWidth size="small" error={!!fieldState.error}>
                                    <InputLabel>Gauge (GG)</InputLabel>
                                    <Select {...field} label="Gauge (GG)" value={field.value || ""}>
                                      <MenuItem value="14">14 GG</MenuItem>
                                      <MenuItem value="16">16 GG</MenuItem>
                                      <MenuItem value="24">24 GG</MenuItem>
                                    </Select>
                                    {fieldState.error && (
                                      <FormHelperText>{fieldState.error.message}</FormHelperText>
                                    )}
                                  </FormControl>
                                )}
                              />
                            </Grid>

                            <Grid item xs={12} sm={4}>
                              <Controller
                                name={`program_details_attributes.${index}.program_type`}
                                control={control}
                                render={({ field, fieldState }) => (
                                  <FormControl fullWidth size="small" error={!!fieldState.error}>
                                    <InputLabel>Type</InputLabel>
                                    <Select {...field} label="Type" value={field.value || ""}>
                                      <MenuItem value="rib">Rib</MenuItem>
                                      <MenuItem value="fine">Fine</MenuItem>
                                    </Select>
                                    {fieldState.error && (
                                      <FormHelperText>{fieldState.error.message}</FormHelperText>
                                    )}
                                  </FormControl>
                                )}
                              />
                            </Grid>

                            {/* Row 2: Weight, Rolls, Texture */}
                            <Grid item xs={12} sm={4}>
                              <Controller
                                name={`program_details_attributes.${index}.weight_in_kilo`}
                                control={control}
                                render={({ field: { onChange, value, ...field }, fieldState }) => (
                                  <TextField
                                    {...field}
                                    label="Weight (kg)"
                                    value={value || ""}
                                    onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
                                    type="number"
                                    size="small"
                                    inputProps={{ step: "0.01" }}
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                    fullWidth
                                  />
                                )}
                              />
                            </Grid>

                            <Grid item xs={12} sm={4}>
                              <Controller
                                name={`program_details_attributes.${index}.number_of_rolls`}
                                control={control}
                                render={({ field: { onChange, value, ...field }, fieldState }) => (
                                  <TextField
                                    {...field}
                                    label="Rolls (Optional)"
                                    value={value || ""}
                                    onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
                                    type="number"
                                    size="small"
                                    inputProps={{ step: "0.01" }}
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                    fullWidth
                                  />
                                )}
                              />
                            </Grid>

                            <Grid item xs={12} sm={4}>
                              <Controller
                                name={`program_details_attributes.${index}.texture`}
                                control={control}
                                render={({ field: { onChange, value, ...field }, fieldState }) => (
                                  <TextField
                                    {...field}
                                    label="Texture (Optional)"
                                    value={value || ""}
                                    onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
                                    type="number"
                                    size="small"
                                    inputProps={{ step: "1" }}
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                    fullWidth
                                  />
                                )}
                              />
                            </Grid>

                            {/* Row 3: Design (Full Width) */}
                            <Grid item xs={12}>
                              <Controller
                                name={`program_details_attributes.${index}.design`}
                                control={control}
                                render={({ field, fieldState }) => (
                                  <TextField
                                    {...field}
                                    label="Design"
                                    size="small"
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                    fullWidth
                                  />
                                )}
                              />
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    );
                    })}

                    {/* Total Weight Card */}
                    <Card variant="outlined" sx={{ bgcolor: "action.hover" }}>
                      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Typography variant="body1" fontWeight="bold">Total Weight</Typography>
                          <Typography variant="body1" fontWeight="bold">{calculateTotalWeight()} kg</Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Stack>
                </Box>
              </Box>

              <Controller
                name="received_note_ids"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl
                    fullWidth
                    margin="normal"
                    error={!!fieldState.error}
                    disabled={!selectedCompanyId}
                  >
                    <InputLabel>Associated Receipts - Optional</InputLabel>
                    <Select
                      {...field}
                      multiple
                      value={field.value || []}
                      disabled={!selectedCompanyId}
                      input={
                        <OutlinedInput label="Associated Receipts - Optional" />
                      }
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {(selected as number[]).map((value) => {
                            const receipt = filteredReceivedNotes.find(
                              (r) => r.id === value
                            );
                            return (
                              <Chip
                                key={value}
                                label={`Receipt #${value} (${
                                  receipt?.counts_display || "N/A"
                                })`}
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {filteredReceivedNotes.map((receipt) => (
                        <MenuItem key={receipt.id} value={receipt.id}>
                          Receipt #{receipt.id} - {receipt.counts_display} (
                          {receipt.total_bags} bags,{" "}
                          {typeof receipt.total_weight === "number"
                            ? receipt.total_weight.toFixed(2)
                            : receipt.total_weight}{" "}
                          kg)
                        </MenuItem>
                      ))}
                    </Select>
                    {!selectedCompanyId && (
                      <FormHelperText>
                        Please select a company first
                      </FormHelperText>
                    )}
                    {filteredReceivedNotes.length === 0 && selectedCompanyId && (
                      <FormHelperText>
                        No receipts available for selected company
                      </FormHelperText>
                    )}
                    {fieldState.error && (
                      <FormHelperText>
                        {fieldState.error.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />

              <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  fullWidth
                >
                  {t('saveChanges')}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setIsEditing(false)}
                  fullWidth
                >
                  {t('cancel')}
                </Button>
              </Stack>
            </Box>
          ) : (
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                {t('programs.programId')}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {program?.id}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary">
                {t('programs.company')}
              </Typography>
              <Typography
                variant="body1"
                onClick={() => navigate(`/companies/${program?.company?.id}`)}
                sx={{
                  mb: 2,
                  cursor: "pointer",
                  color: "primary.main",
                  textDecoration: "underline",
                  "&:hover": { color: "primary.dark" },
                }}
              >
                {program?.company?.name}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary">
                {t('programs.lot')}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {program?.lot || "-"}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary">
                {t('status')}
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Chip
                  label={
                    program?.status === "not_started"
                      ? t('programs.statusNotStarted')
                      : program?.status === "in_progress"
                      ? t('programs.statusInProgress')
                      : program?.status === "completed"
                      ? t('programs.statusCompleted')
                      : t('programs.statusDelivered')
                  }
                  sx={{
                    bgcolor:
                      program?.status === "not_started"
                        ? "#9e9e9e"
                        : program?.status === "in_progress"
                        ? "#fdd835"
                        : program?.status === "completed"
                        ? "#66bb6a"
                        : "#2196f3",
                    color: program?.status === "in_progress" ? "#000" : "#fff",
                  }}
                />
              </Box>

              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                {t('programs.programDetails')}
              </Typography>

              {/* Desktop Table View */}
              <Box sx={{ display: { xs: "none", md: "block" }, mb: 2 }}>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('programs.dia')}</TableCell>
                        <TableCell>{t('programs.gauge')}</TableCell>
                        <TableCell>{t('programs.type')}</TableCell>
                        <TableCell>{t('status')}</TableCell>
                        <TableCell>{t('programs.weightKg')}</TableCell>
                        <TableCell>{t('programs.numberOfRolls')}</TableCell>
                        <TableCell>{t('programs.texture')}</TableCell>
                        <TableCell>{t('programs.progress')}</TableCell>
                        <TableCell>{t('programs.design')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {program?.program_details.map((detail, index) => {
                        const progress = calculateProgress(detail);
                        return (
                          <TableRow key={index}>
                            <TableCell>
                              <DiaCircle
                                dia={detail.dia}
                                gauge={detail.gauge}
                                machineType={detail.program_type}
                                machines={machines}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <GaugeChip gauge={detail.gauge} />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={detail.program_type}
                                size="small"
                                sx={{
                                  bgcolor: detail.program_type === "rib" ? '#e0e0e0' : '#757575',
                                  color: detail.program_type === "rib" ? '#000' : '#fff',
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={
                                  detail.status === 'completed' ? t('programs.statusCompleted') :
                                  detail.status === 'in_progress' ? t('programs.statusInProgress') :
                                  t('programs.statusNotStarted')
                                }
                                size="small"
                                color={
                                  detail.status === 'completed' ? 'success' :
                                  detail.status === 'in_progress' ? 'primary' :
                                  'default'
                                }
                              />
                            </TableCell>
                            <TableCell>{detail.weight_in_kilo}</TableCell>
                            <TableCell>{detail.number_of_rolls ? formatInteger(detail.number_of_rolls) : "-"}</TableCell>
                            <TableCell>{(detail as any).texture || "-"}</TableCell>
                            <TableCell sx={{ minWidth: 250 }}>
                              <Stack spacing={1.5}>
                                <ProgressBar
                                  type="weight"
                                  done={progress.completedWeight}
                                  total={detail.weight_in_kilo}
                                  size="small"
                                />
                                {progress.hasRollsConstraint && (
                                  <ProgressBar
                                    type="rolls"
                                    done={progress.completedRolls}
                                    total={detail.number_of_rolls!}
                                    remainingWeight={detail.weight_in_kilo - progress.completedWeight}
                                    size="small"
                                  />
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell>{detail.design || "-"}</TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow sx={{ bgcolor: "action.hover" }}>
                        <TableCell colSpan={4}>
                          <strong>Total Weight</strong>
                        </TableCell>
                        <TableCell>
                          <strong>
                            {typeof program?.total_weight === "number"
                              ? program.total_weight.toFixed(2)
                              : Number(program?.total_weight || 0).toFixed(
                                  2
                                )}{" "}
                            kg
                          </strong>
                        </TableCell>
                        <TableCell colSpan={4}></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Mobile Card View */}
              <Box sx={{ display: { xs: "block", md: "none" }, mb: 2 }}>
                <Stack spacing={2}>
                  {program?.program_details.map((detail, index) => {
                    const progress = calculateProgress(detail);
                    return (
                      <Card key={index} variant="outlined">
                        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
                            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                              <DiaCircle
                                dia={detail.dia}
                                gauge={detail.gauge}
                                machineType={detail.program_type}
                                machines={machines}
                                size="small"
                              />
                              <GaugeChip gauge={detail.gauge} />
                              <Chip
                                label={detail.program_type}
                                size="small"
                                sx={{
                                  bgcolor: detail.program_type === "rib" ? '#e0e0e0' : '#757575',
                                  color: detail.program_type === "rib" ? '#000' : '#fff',
                                }}
                              />
                              <Chip
                                label={
                                  detail.status === 'completed' ? 'Completed' :
                                  detail.status === 'in_progress' ? 'In Progress' :
                                  'Not Started'
                                }
                                size="small"
                                color={
                                  detail.status === 'completed' ? 'success' :
                                  detail.status === 'in_progress' ? 'primary' :
                                  'default'
                                }
                              />
                            </Box>
                          </Box>
                          
                          <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 1, mb: 2 }}>
                            <Typography variant="body2" color="text.secondary" fontWeight="bold">Weight:</Typography>
                            <Typography variant="body2">{detail.weight_in_kilo} kg</Typography>
                            
                            {detail.number_of_rolls && (
                              <>
                                <Typography variant="body2" color="text.secondary" fontWeight="bold">Rolls:</Typography>
                                <Typography variant="body2">{formatInteger(detail.number_of_rolls)}</Typography>
                              </>
                            )}
                            
                            {(detail as any).texture && (
                              <>
                                <Typography variant="body2" color="text.secondary" fontWeight="bold">Texture:</Typography>
                                <Typography variant="body2">{(detail as any).texture}</Typography>
                              </>
                            )}
                            
                            {detail.design && (
                              <>
                                <Typography variant="body2" color="text.secondary" fontWeight="bold">Design:</Typography>
                                <Typography variant="body2">{detail.design}</Typography>
                              </>
                            )}
                          </Box>

                          {/* Progress Section */}
                          <Divider sx={{ my: 1.5 }} />
                          <Stack spacing={1.5}>
                            <ProgressBar
                              type="weight"
                              done={progress.completedWeight}
                              total={detail.weight_in_kilo}
                              size="small"
                            />
                            {progress.hasRollsConstraint && (
                              <ProgressBar
                                type="rolls"
                                done={progress.completedRolls}
                                total={detail.number_of_rolls!}
                                remainingWeight={detail.weight_in_kilo - progress.completedWeight}
                                size="small"
                              />
                            )}
                          </Stack>
                        </CardContent>
                      </Card>
                    );
                  })}
                  
                  {/* Total Weight Card */}
                  <Card variant="outlined" sx={{ bgcolor: "action.hover" }}>
                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="body1" fontWeight="bold">Total Weight</Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {typeof program?.total_weight === "number"
                            ? program.total_weight.toFixed(2)
                            : Number(program?.total_weight || 0).toFixed(2)}{" "}
                          kg
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Stack>
              </Box>

              {/* Employee Shift Details - Hidden for company_rep users */}
              {!isCompanyRep && canAccessResource('employee_shifts') && (
                <>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Employee Shift Details
                  </Typography>
                  {program?.employee_shift_details &&
                  program.employee_shift_details.length > 0 ? (
                <>
                  {/* Desktop Table View */}
                  <Box sx={{ display: { xs: "none", md: "block" }, mb: 2 }}>
                    <TableContainer
                      component={Paper}
                      variant="outlined"
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Shift</TableCell>
                            <TableCell>Employee</TableCell>
                            <TableCell>Dia</TableCell>
                            <TableCell>Gauge</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Rolls</TableCell>
                            <TableCell>Weight (kg)</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {program.employee_shift_details.map((detail) => (
                            <TableRow
                              key={detail.id}
                              hover
                              sx={{ cursor: "pointer" }}
                              onClick={() =>
                                navigate(
                                  `/employee-shifts/${detail.employee_shift.id}`
                                )
                              }
                            >
                              <TableCell>
                                {new Date(
                                  detail.employee_shift.shift_date
                                ).toLocaleDateString()}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={
                                    detail.employee_shift.shift_time === "day"
                                      ? "☀️ Day"
                                      : "🌙 Night"
                                  }
                                  size="small"
                                  sx={{
                                    bgcolor:
                                      detail.employee_shift.shift_time === "day"
                                        ? "#fff9c4"
                                        : "#b3e5fc",
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                {detail.employee_shift.employee.name}
                              </TableCell>
                              <TableCell>
                                <DiaCircle
                                  dia={detail.dia}
                                  gauge={detail.gauge}
                                  machineType={detail.machine_type}
                                  machines={machines}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <GaugeChip gauge={detail.gauge} />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={detail.machine_type}
                                  size="small"
                                  sx={{
                                    bgcolor: detail.machine_type === "rib" ? '#e0e0e0' : '#757575',
                                    color: detail.machine_type === "rib" ? '#000' : '#fff',
                                  }}
                                />
                              </TableCell>
                              <TableCell>{detail.rolls}</TableCell>
                              <TableCell>
                                {typeof detail.weight === "number"
                                  ? detail.weight.toFixed(2)
                                  : detail.weight}
                              </TableCell>
                            </TableRow>
                          ))}
                          <TableRow sx={{ bgcolor: "action.hover" }}>
                            <TableCell colSpan={6}>
                              <strong>Total</strong>
                            </TableCell>
                            <TableCell>
                              <strong>
                                {program.employee_shift_details.reduce(
                                  (sum, detail) => sum + detail.rolls,
                                  0
                                )}
                              </strong>
                            </TableCell>
                            <TableCell>
                              <strong>
                                {program.employee_shift_details
                                  .reduce(
                                    (sum, detail) => sum + Number(detail.weight),
                                    0
                                  )
                                  .toFixed(2)}{" "}
                                kg
                              </strong>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>

                  {/* Mobile Card View */}
                  <Box sx={{ display: { xs: "block", md: "none" }, mb: 2 }}>
                    <Stack spacing={2}>
                      {program.employee_shift_details.map((detail) => (
                        <Card 
                          key={detail.id} 
                          variant="outlined"
                          sx={{ 
                            cursor: "pointer",
                            "&:hover": { bgcolor: "action.hover" }
                          }}
                          onClick={() =>
                            navigate(
                              `/employee-shifts/${detail.employee_shift.id}`
                            )
                          }
                        >
                          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
                              <Box>
                                <Typography variant="body2" fontWeight="bold">
                                  {detail.employee_shift.employee.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(detail.employee_shift.shift_date).toLocaleDateString()}
                                </Typography>
                              </Box>
                              <Chip
                                label={
                                  detail.employee_shift.shift_time === "day"
                                    ? "☀️ Day"
                                    : "🌙 Night"
                                }
                                size="small"
                                sx={{
                                  bgcolor:
                                    detail.employee_shift.shift_time === "day"
                                      ? "#fff9c4"
                                      : "#b3e5fc",
                                }}
                              />
                            </Box>
                            
                            <Box sx={{ display: "flex", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
                              <DiaCircle
                                dia={detail.dia}
                                gauge={detail.gauge}
                                machineType={detail.machine_type}
                                machines={machines}
                                size="small"
                              />
                              <GaugeChip gauge={detail.gauge} />
                              <Chip
                                label={detail.machine_type}
                                size="small"
                                sx={{
                                  bgcolor: detail.machine_type === "rib" ? '#e0e0e0' : '#757575',
                                  color: detail.machine_type === "rib" ? '#000' : '#fff',
                                }}
                              />
                            </Box>

                            <Box sx={{ display: "flex", justifyContent: "space-around", pt: 1, borderTop: "1px solid", borderColor: "divider" }}>
                              <Box sx={{ textAlign: "center" }}>
                                <Typography variant="caption" color="text.secondary" display="block">Rolls</Typography>
                                <Typography variant="body2" fontWeight="bold">{detail.rolls}</Typography>
                              </Box>
                              <Box sx={{ textAlign: "center" }}>
                                <Typography variant="caption" color="text.secondary" display="block">Weight</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {typeof detail.weight === "number"
                                    ? detail.weight.toFixed(2)
                                    : detail.weight} kg
                                </Typography>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                      
                      {/* Total Card */}
                      <Card variant="outlined" sx={{ bgcolor: "action.hover" }}>
                        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                          <Box sx={{ display: "flex", justifyContent: "space-around" }}>
                            <Box sx={{ textAlign: "center" }}>
                              <Typography variant="caption" color="text.secondary" display="block">Total Rolls</Typography>
                              <Typography variant="body1" fontWeight="bold">
                                {program.employee_shift_details.reduce(
                                  (sum, detail) => sum + detail.rolls,
                                  0
                                )}
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: "center" }}>
                              <Typography variant="caption" color="text.secondary" display="block">Total Weight</Typography>
                              <Typography variant="body1" fontWeight="bold">
                                {program.employee_shift_details
                                  .reduce(
                                    (sum, detail) => sum + Number(detail.weight),
                                    0
                                  )
                                  .toFixed(2)} kg
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Stack>
                  </Box>
                </>
              ) : (
                <Typography variant="body1" sx={{ mb: 2 }}>
                  No employee shifts recorded yet
                </Typography>
              )}
                </>
              )}

              <Typography variant="subtitle2" color="text.secondary">
                {t('programs.associatedReceipts')}
              </Typography>
              <Box sx={{ mb: 2 }}>
                {program?.received_notes && program.received_notes.length > 0 ? (
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    {program.received_notes.map((receipt) => (
                      <Card
                        key={receipt.id}
                        variant="outlined"
                        onClick={() => navigate(`/received-notes/${receipt.id}`)}
                        sx={{
                          cursor: "pointer",
                          "&:hover": { bgcolor: "action.hover" },
                        }}
                      >
                        <CardContent sx={{ py: { xs: 1.5, sm: 1 }, px: { xs: 1.5, sm: 2 }, "&:last-child": { pb: { xs: 1.5, sm: 1 } } }}>
                          <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
                            <strong>Receipt #{receipt.id}</strong>
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "0.813rem", sm: "0.875rem" }, wordBreak: "break-word" }}>
                            Counts: {receipt.counts_display}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "0.813rem", sm: "0.875rem" } }}>
                            Bags: {receipt.total_bags} | Weight:{" "}
                            {typeof receipt.total_weight === "number"
                              ? receipt.total_weight.toFixed(2)
                              : receipt.total_weight}{" "}
                            kg
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body1">
                    No received notes associated
                  </Typography>
                )}
              </Box>

              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Deliveries
              </Typography>
              {program?.deliveries && program.deliveries.length > 0 ? (
                <>
                  {/* Desktop Table View */}
                  <Box sx={{ display: { xs: "none", md: "block" }, mb: 2 }}>
                    <TableContainer
                      component={Paper}
                      variant="outlined"
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Person</TableCell>
                            <TableCell>Phone</TableCell>
                            <TableCell>Vehicle</TableCell>
                            <TableCell>Specs</TableCell>
                            <TableCell>Rolls</TableCell>
                            <TableCell>Weight (kg)</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {program.deliveries.map((delivery) => (
                            <TableRow
                              key={delivery.id}
                              hover
                              sx={{ cursor: "pointer" }}
                              onClick={() => navigate(`/deliveries/${delivery.id}`)}
                            >
                              <TableCell>{delivery.id}</TableCell>
                              <TableCell>
                                {new Date(delivery.created_at).toLocaleDateString()}
                              </TableCell>
                              <TableCell>{delivery.delivery_person_name}</TableCell>
                              <TableCell>{delivery.delivery_person_phone}</TableCell>
                              <TableCell>{delivery.delivery_vehicle_number}</TableCell>
                              <TableCell>{delivery.specs_display}</TableCell>
                              <TableCell>{formatInteger(delivery.calculated_total_rolls)}</TableCell>
                              <TableCell>
                                {typeof delivery.calculated_total_weight === "number"
                                  ? delivery.calculated_total_weight.toFixed(2)
                                  : delivery.calculated_total_weight}
                              </TableCell>
                            </TableRow>
                          ))}
                          <TableRow sx={{ bgcolor: "action.hover" }}>
                            <TableCell colSpan={6}>
                              <strong>Total</strong>
                            </TableCell>
                            <TableCell>
                              <strong>
                                {formatInteger(program.deliveries.reduce(
                                  (sum, delivery) => sum + delivery.calculated_total_rolls,
                                  0
                                ))}
                              </strong>
                            </TableCell>
                            <TableCell>
                              <strong>
                                {program.deliveries
                                  .reduce(
                                    (sum, delivery) =>
                                      sum + Number(delivery.calculated_total_weight),
                                    0
                                  )
                                  .toFixed(2)}{" "}
                                kg
                              </strong>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>

                  {/* Mobile Card View */}
                  <Box sx={{ display: { xs: "block", md: "none" }, mb: 2 }}>
                    <Stack spacing={2}>
                      {program.deliveries.map((delivery) => (
                        <Card 
                          key={delivery.id} 
                          variant="outlined"
                          sx={{ 
                            cursor: "pointer",
                            "&:hover": { bgcolor: "action.hover" }
                          }}
                          onClick={() => navigate(`/deliveries/${delivery.id}`)}
                        >
                          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
                              <Box>
                                <Typography variant="body2" fontWeight="bold">
                                  Delivery #{delivery.id}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(delivery.created_at).toLocaleDateString()}
                                </Typography>
                              </Box>
                            </Box>
                            
                            <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 1, mb: 1.5 }}>
                              <Typography variant="body2" color="text.secondary" fontWeight="bold">Person:</Typography>
                              <Typography variant="body2">{delivery.delivery_person_name}</Typography>
                              
                              <Typography variant="body2" color="text.secondary" fontWeight="bold">Phone:</Typography>
                              <Typography variant="body2">{delivery.delivery_person_phone}</Typography>
                              
                              <Typography variant="body2" color="text.secondary" fontWeight="bold">Vehicle:</Typography>
                              <Typography variant="body2">{delivery.delivery_vehicle_number}</Typography>
                              
                              <Typography variant="body2" color="text.secondary" fontWeight="bold">Specs:</Typography>
                              <Typography variant="body2">{delivery.specs_display}</Typography>
                            </Box>

                            <Box sx={{ display: "flex", justifyContent: "space-around", pt: 1, borderTop: "1px solid", borderColor: "divider" }}>
                              <Box sx={{ textAlign: "center" }}>
                                <Typography variant="caption" color="text.secondary" display="block">Rolls</Typography>
                                <Typography variant="body2" fontWeight="bold">{formatInteger(delivery.calculated_total_rolls)}</Typography>
                              </Box>
                              <Box sx={{ textAlign: "center" }}>
                                <Typography variant="caption" color="text.secondary" display="block">Weight</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {typeof delivery.calculated_total_weight === "number"
                                    ? delivery.calculated_total_weight.toFixed(2)
                                    : delivery.calculated_total_weight} kg
                                </Typography>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                      
                      {/* Total Card */}
                      <Card variant="outlined" sx={{ bgcolor: "action.hover" }}>
                        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                          <Box sx={{ display: "flex", justifyContent: "space-around" }}>
                            <Box sx={{ textAlign: "center" }}>
                              <Typography variant="caption" color="text.secondary" display="block">Total Rolls</Typography>
                              <Typography variant="body1" fontWeight="bold">
                                {formatInteger(program.deliveries.reduce(
                                  (sum, delivery) => sum + delivery.calculated_total_rolls,
                                  0
                                ))}
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: "center" }}>
                              <Typography variant="caption" color="text.secondary" display="block">Total Weight</Typography>
                              <Typography variant="body1" fontWeight="bold">
                                {program.deliveries
                                  .reduce(
                                    (sum, delivery) =>
                                      sum + Number(delivery.calculated_total_weight),
                                    0
                                  )
                                  .toFixed(2)} kg
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Stack>
                  </Box>
                </>
              ) : (
                <Typography variant="body1" sx={{ mb: 2 }}>
                  No deliveries recorded yet
                </Typography>
              )}

              <Typography variant="subtitle2" color="text.secondary">
                {t('created')}
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {new Date(program?.created_at || "").toLocaleDateString()}
              </Typography>

              {(canUpdate('programs') || canDelete('programs')) && (
                <Stack direction="row" spacing={2} className="no-print">
                  {canUpdate('programs') && (
                    <Button
                      variant="contained"
                      startIcon={<EditIcon />}
                      onClick={() => setIsEditing(true)}
                      fullWidth
                    >
                      {t('edit')}
                    </Button>
                  )}
                  {canDelete('programs') && (
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={handleDelete}
                      fullWidth
                    >
                      {t('delete')}
                    </Button>
                  )}
                </Stack>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Share Dialog */}
      <Dialog 
        open={shareDialogOpen} 
        onClose={handleCloseShareDialog} 
        maxWidth="sm" 
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            m: { xs: 2, sm: 3 },
            maxWidth: { xs: 'calc(100% - 32px)', sm: 600 },
            width: '100%',
            borderRadius: { xs: 2, sm: 2 },
          },
        }}
      >
        <DialogTitle 
          sx={{ 
            fontSize: { xs: '1rem', sm: '1.25rem' }, 
            px: { xs: 2, sm: 3 },
            pt: { xs: 2, sm: 3 },
            pb: { xs: 1, sm: 2 },
            fontWeight: 600,
          }}
        >
          {t('programs.shareProgram')}
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              mb: { xs: 2, sm: 2.5 },
              fontSize: { xs: '0.813rem', sm: '0.875rem' },
              lineHeight: { xs: 1.5, sm: 1.6 },
            }}
          >
            Share this link with your customer via SMS or any messaging app. They can view the program status without logging in.
          </Typography>
          
          {/* SMS Message Section */}
          <Typography 
            variant="subtitle2" 
            sx={{ 
              mb: 1, 
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <SmsIcon fontSize="small" color="primary" />
            {t('programs.shareViaSms')}
          </Typography>
          <Box
            sx={{
              p: { xs: 1.5, sm: 2 },
              bgcolor: "primary.50",
              borderRadius: 1,
              display: "flex",
              flexDirection: "column",
              gap: { xs: 1.5, sm: 1 },
              border: "1px solid",
              borderColor: "primary.200",
              mb: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: '0.813rem', sm: '0.875rem' },
                lineHeight: 1.6,
                color: "text.primary",
              }}
            >
              {smsMessage}
            </Typography>
            <Button
              onClick={handleCopySms}
              variant="contained"
              size="medium"
              startIcon={<SmsIcon />}
              fullWidth
              sx={{ 
                minWidth: 'auto',
                whiteSpace: 'nowrap',
              }}
            >
              Copy SMS Message
            </Button>
          </Box>

          {/* Link Only Section */}
          <Typography 
            variant="subtitle2" 
            sx={{ 
              mb: 1, 
              fontWeight: 600,
            }}
          >
            Link Only
          </Typography>
          <Box
            sx={{
              p: { xs: 1.5, sm: 2 },
              bgcolor: "grey.100",
              borderRadius: 1,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              gap: { xs: 1.5, sm: 1 },
              border: "1px solid",
              borderColor: "grey.300",
            }}
          >
            <Box
              sx={{
                flex: 1,
                minWidth: 0, // Allow text to shrink
                overflow: "hidden",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  wordBreak: "break-all",
                  fontFamily: "monospace",
                  fontSize: { xs: '0.688rem', sm: '0.813rem' },
                  overflowWrap: "anywhere",
                  lineHeight: 1.5,
                  color: "text.primary",
                }}
              >
                {shareUrl}
              </Typography>
            </Box>
            <Button
              onClick={handleCopyLink}
              variant="outlined"
              size="medium"
              startIcon={<ContentCopyIcon />}
              sx={{ 
                display: { xs: 'flex', sm: 'none' },
                minWidth: 'auto',
                whiteSpace: 'nowrap',
              }}
            >
              Copy Link
            </Button>
            <IconButton
              onClick={handleCopyLink}
              color="primary"
              size="small"
              title="Copy to clipboard"
              sx={{ 
                display: { xs: 'none', sm: 'inline-flex' },
                flexShrink: 0,
              }}
            >
              <ContentCopyIcon />
            </IconButton>
          </Box>
        </DialogContent>
        <DialogActions 
          sx={{ 
            px: { xs: 2, sm: 3 }, 
            pb: { xs: 2, sm: 2.5 },
            pt: { xs: 1, sm: 1 },
          }}
        >
          <Button 
            onClick={handleCloseShareDialog} 
            variant="outlined"
            fullWidth 
            sx={{ display: { xs: 'block', sm: 'none' } }}
          >
            Close
          </Button>
          <Button 
            onClick={handleCloseShareDialog} 
            sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Container>
  );
}
